import React, { FC, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { ExtraPayTypeOptions, ExtraPayUnit } from '../../../redux/api/settlement/settlement.model';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import TwAutocomplete, {
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import Typography from '@mui/material/Typography';
import { Divider, IconButton, styled } from '@mui/material';
import NumberUtils from '../../../utils/numberUtils';
import {
  TRIP_DRIVER_PAY_TYPE,
  TRIP_DRIVER_PAY_UNIT,
  TripDriverOtherPay
} from '../../../redux/api/trips/trips.model';

const otherLoadPayTypeAutocompleteOptions: TwAutocompleteOption[] = Object.entries(
  ExtraPayTypeOptions
).map(([key, value]) => ({
  id: key,
  label: value,
  value: key
}));

const otherLoadPayUnitAutocompleteOptions: TwAutocompleteOption[] = Object.entries(
  ExtraPayUnit
).map(([key, value]) => ({
  id: key,
  label: value,
  value: key
}));

const AddItem = styled(Box)(({}) => ({
  cursor: 'pointer',
  color: '#3A4DE9',
  fontWeight: 500,
  fontSize: '14px',
  marginTop: '12px',
  width: '80px'
}));

const TotalOtherLoadPay = styled(Box)(({}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  margin: '20px 0',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  padding: '20px 12px',
  backgroundColor: 'white'
}));

export interface OtherPayErrors {
  itemIndex: number;
  missingFields: string[];
}

type OtherPayRowData = Partial<TripDriverOtherPay>;
export interface OtherLoadPayProps {
  initialData: OtherPayRowData[];
  onDataUpdate?: (data: OtherPayRowData[], total: number) => void;
  errors?: OtherPayErrors[];
}

const OtherLoadPay: FC<OtherLoadPayProps> = ({ initialData, onDataUpdate, errors }) => {
  const [rowsData, setRowsData] = useState<OtherPayRowData[]>(initialData);

  useEffect(() => {
    onDataUpdate && onDataUpdate(rowsData, totalOtherLoadPay);
  }, [rowsData]);

  const handleAddRow = (): void => {
    const newRowsData = [...rowsData, {}];
    setRowsData(newRowsData);
    onDataUpdate && onDataUpdate(newRowsData, totalOtherLoadPay);
  };

  const handleDataUpdate = (key: number, newData: OtherPayRowData): void => {
    const newRowsData = [...rowsData];
    newRowsData[key] = newData;
    setRowsData(newRowsData);
    onDataUpdate && onDataUpdate(newRowsData, totalOtherLoadPay);
  };

  const handleDeleteRow = (key: number): void => {
    const newRowsData = rowsData.filter((_, index) => index !== key);
    setRowsData(newRowsData);
    onDataUpdate && onDataUpdate(newRowsData, totalOtherLoadPay);
  };

  const showOtherLoadPayTotal = (): JSX.Element => {
    return (
      <TotalOtherLoadPay>
        <Typography variant="body2" fontWeight="400" fontSize="14px">
          Total other trip pay
        </Typography>
        <Typography variant="body2" fontWeight="600" fontSize="14px">
          {NumberUtils.getCurrency(totalOtherLoadPay)}
        </Typography>
      </TotalOtherLoadPay>
    );
  };

  const totalOtherLoadPay = useMemo<number>(() => {
    return rowsData.reduce((acc, row) => {
      if (row.pay_type !== 'DEDUCTION') {
        return acc + (row.quantity || 1) * (row.rate || 0);
      } else {
        return acc - (row.quantity || 1) * (row.rate || 0);
      }
    }, 0);
  }, [
    JSON.stringify(rowsData.map((row) => row.quantity)),
    JSON.stringify(rowsData.map((row) => row.rate)),
    JSON.stringify(rowsData.map((row) => row.pay_type))
  ]);

  return (
    <Box>
      <Grid container direction="column" rowSpacing={1} gap={'12px'} marginTop={'20px'}>
        <Typography variant="body2" fontWeight="500" fontSize="16px">
          Other trip pay
        </Typography>

        {rowsData.map((row, index) => (
          <div key={index}>
            <OtherPayTableRow
              id={index}
              data={row}
              onDataUpdate={handleDataUpdate}
              onDeleteIconClick={handleDeleteRow}
              errors={
                // Find errors where the itemIndex matches this item index and return its missing fields
                errors?.find((error) => error.itemIndex === index)?.missingFields || []
              }
            />
            {index == rowsData.length - 1 ? null : <Divider />}
          </div>
        ))}
      </Grid>

      {rowsData.length <= 14 && <AddItem onClick={handleAddRow}>+ Add item</AddItem>}

      <Box>{showOtherLoadPayTotal()}</Box>
    </Box>
  );
};

export default OtherLoadPay;

interface IOtherPayTableRow {
  id: number;
  data?: OtherPayRowData;
  onDataUpdate?: (key: number, newData: OtherPayRowData) => void;
  onDeleteIconClick?: (key: number) => void;
  errors?: string[];
}

const OtherPayTableRow: FC<IOtherPayTableRow> = ({
  id,
  data,
  onDataUpdate,
  onDeleteIconClick,
  errors
}) => {
  const [selectedType, setSelectedType] = useState<TwAutocompleteOption | null | undefined>(null);
  const [selectedUnit, setSelectedUnit] = useState<TwAutocompleteOption | null | undefined>(null);

  useEffect(() => {
    if (data?.pay_type && data.pay_type !== selectedType?.value) {
      setSelectedType(
        otherLoadPayTypeAutocompleteOptions.find((option) => option.value === data.pay_type)
      );
    }
  }, [data?.pay_type]);

  useEffect(() => {
    if (data?.unit && data.unit !== selectedUnit?.value) {
      setSelectedUnit(
        otherLoadPayUnitAutocompleteOptions.find((option) => option.value === data.unit)
      );
    }
  }, [data?.unit]);

  useEffect(() => {
    if (selectedUnit?.value === 'FLAT') {
      onDataUpdate && onDataUpdate(id, { ...data, quantity: 1 });
    } else {
      onDataUpdate && onDataUpdate(id, { ...data, quantity: data?.quantity || 0 });
    }
  }, [selectedUnit?.value]);

  return (
    <Grid
      container
      item
      columnSpacing={1}
      position={'relative'}
      sx={{
        padding: '0 40px 4px 4px',
        paddingTop: '12px !important',
        border: '1px solid #E5E7EB',
        marginLeft: '0',
        width: '100%',
        borderRadius: '8px',
        backgroundColor: 'white'
      }}
    >
      <Grid item xs={8}>
        <TwField
          name={`${id}-description`}
          value={data?.description}
          onInputChange={(e): void => {
            onDataUpdate && onDataUpdate(id, { ...data, description: e.target.value });
          }}
          placeholder="Add name"
          error={errors?.includes('Description')}
        />
      </Grid>
      <Grid item xs={4}>
        <TwAutocomplete
          name={`${id}-type`}
          optionList={otherLoadPayTypeAutocompleteOptions}
          value={selectedType}
          sortList={false}
          onChange={(_, newValue): void => {
            if (newValue) {
              onDataUpdate &&
                onDataUpdate(id, { ...data, pay_type: newValue.value as TRIP_DRIVER_PAY_TYPE });
            }
          }}
          error={errors?.includes('Pay type')}
        />
      </Grid>
      <Grid item xs={6}>
        <TwAutocomplete
          name={`${id}-unit`}
          optionList={otherLoadPayUnitAutocompleteOptions}
          value={selectedUnit}
          sortList={false}
          onChange={(_, newValue): void => {
            if (newValue) {
              onDataUpdate &&
                onDataUpdate(id, { ...data, unit: newValue.value as TRIP_DRIVER_PAY_UNIT });
            }
          }}
          error={errors?.includes('Unit')}
        />
      </Grid>
      <Grid item xs={3}>
        {selectedUnit?.value !== 'FLAT' ? (
          <TwField
            name={`${id}-quantity`}
            inputType={TwInputTypes.number}
            value={data?.quantity || ''}
            onInputChange={(e): void => {
              onDataUpdate && onDataUpdate(id, { ...data, quantity: Number(e.target.value) });
            }}
            placeholder="QTY"
            error={errors?.includes('Quantity')}
          />
        ) : (
          <TwField
            name={`${id}-quantity`}
            inputType={TwInputTypes.number}
            value={'-'}
            placeholder="-"
            disabled
          />
        )}
      </Grid>
      <Grid item xs={3}>
        <TwField
          inputType={TwInputTypes.number}
          name={`${id}-rate`}
          value={data?.rate || ''}
          startAdorment={
            <Typography variant="body2" fontWeight="400" fontSize="14px" color="#9CA3AF">
              $
            </Typography>
          }
          onInputChange={(e): void => {
            onDataUpdate && onDataUpdate(id, { ...data, rate: Number(e.target.value) });
          }}
          error={errors?.includes('Rate')}
        />
      </Grid>
      <Grid item xs={2}>
        <IconButton
          size="small"
          onClick={(): void => {
            onDeleteIconClick && onDeleteIconClick(id);
          }}
          sx={{
            position: 'absolute',
            right: '5px',
            top: '18px'
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  );
};
